const POSTER_TIME_SECONDS = 0.1;
// Twice the 240px card width, so the thumbnail stays sharp on high-density screens.
const POSTER_MAX_WIDTH = 480;
const POSTER_QUALITY = 0.82;
const POSTER_TIMEOUT_MS = 10_000;

/**
 * Draws an early frame of an MP4 as a small JPEG data URL, so the result needs no cleanup. Rejects
 * when no frame can be drawn (undecodable video, no picture, timeout) or when `signal` aborts.
 * The object URL used for decoding is revoked on every outcome.
 */
export function drawVideoPoster(bytes: Uint8Array, signal: AbortSignal): Promise<string> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(signal.reason);
      return;
    }
    const url = URL.createObjectURL(new Blob([new Uint8Array(bytes)], { type: "video/mp4" }));
    const video = document.createElement("video");
    let settled = false;
    const timer = window.setTimeout(
      () => fail(new Error("Timed out drawing a video frame")),
      POSTER_TIMEOUT_MS,
    );

    function settle() {
      if (settled) return false;
      settled = true;
      window.clearTimeout(timer);
      video.removeAttribute("src");
      video.load();
      URL.revokeObjectURL(url);
      return true;
    }
    function fail(error: unknown) {
      if (settle()) reject(error);
    }
    function capture() {
      if (settled) return;
      try {
        const { videoWidth, videoHeight } = video;
        if (!videoWidth || !videoHeight) throw new Error("The video has no picture");
        const scale = Math.min(1, POSTER_MAX_WIDTH / videoWidth);
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(videoWidth * scale));
        canvas.height = Math.max(1, Math.round(videoHeight * scale));
        const context = canvas.getContext("2d");
        if (!context) throw new Error("Canvas is unavailable");
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        const poster = canvas.toDataURL("image/jpeg", POSTER_QUALITY);
        if (settle()) resolve(poster);
      } catch (error) {
        fail(error);
      }
    }

    signal.addEventListener("abort", () => fail(signal.reason), { once: true });
    video.addEventListener("error", () => fail(new Error("The video cannot be decoded")), {
      once: true,
    });
    video.addEventListener(
      "loadedmetadata",
      () => {
        video.addEventListener("seeked", capture, { once: true });
        video.currentTime = Number.isFinite(video.duration)
          ? Math.min(POSTER_TIME_SECONDS, video.duration / 2)
          : POSTER_TIME_SECONDS;
      },
      { once: true },
    );
    video.muted = true;
    video.playsInline = true;
    video.preload = "auto";
    video.src = url;
  });
}
